import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import type {
  ConversationResponse as Conversation,
  TagCount,
} from "../../../api/generated";
import { BulkTagToolbar } from "./BulkTagToolbar";
import { ConversationList } from "./ConversationList";

const DRAWER_WIDTH = 350;

interface HistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  conversations: Conversation[] | undefined;
  isLoading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onAddTag: (id: string, tag: string) => void;
  onRemoveTag: (id: string, tag: string) => void;
  filterTab: number;
  onFilterChange: (tab: number) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  activeTagFilters: string[];
  onTagFilterAdd: (tag: string) => void;
  onTagFilterRemove: (tag: string) => void;
  onTagFilterClear: () => void;
  tagSuggestions: TagCount[];
  bulkMode: boolean;
  onBulkModeToggle: () => void;
  selectedIds: Set<string>;
  onBulkToggle: (id: string) => void;
  onBulkApply: (tags: string[]) => void;
  onBulkCancel: () => void;
}

const MODE_FILTERS = [
  { label: "All" },
  { label: "Pre-Call" },
  { label: "Post-Call" },
];

export function HistoryDrawer({
  open,
  onClose,
  conversations,
  isLoading,
  selectedId,
  onSelect,
  onDelete,
  onRename,
  onAddTag,
  onRemoveTag,
  filterTab,
  onFilterChange,
  searchQuery,
  onSearchChange,
  activeTagFilters,
  onTagFilterAdd,
  onTagFilterRemove,
  onTagFilterClear,
  tagSuggestions,
  bulkMode,
  onBulkModeToggle,
  selectedIds,
  onBulkToggle,
  onBulkApply,
  onBulkCancel,
}: HistoryDrawerProps) {
  const hasFilters = !!searchQuery || activeTagFilters.length > 0;

  // Top 5 popular tags for quick-filter (exclude already active ones)
  const activeSet = new Set(activeTagFilters);
  const quickTags = tagSuggestions
    .filter((t) => !activeSet.has(t.name))
    .slice(0, 5);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
        }}
      >
        <Typography variant="h6">Chat History</Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            onClick={onBulkModeToggle}
            size="small"
            color={bulkMode ? "primary" : "default"}
            title="Bulk tag mode"
          >
            <CheckBoxOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      {/* Search input */}
      <Box sx={{ px: 2, pb: 1 }}>
        <TextField
          size="small"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onSearchChange("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
          sx={{ "& .MuiInputBase-input": { fontSize: "0.85rem" } }}
        />
      </Box>
      {/* Active tag filters */}
      {activeTagFilters.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.5,
            px: 2,
            pb: 1,
          }}
        >
          {activeTagFilters.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              color="primary"
              onDelete={() => onTagFilterRemove(tag)}
              sx={{ height: 22, fontSize: "0.75rem" }}
            />
          ))}
          <Button
            size="small"
            onClick={onTagFilterClear}
            sx={{ minWidth: 0, fontSize: "0.75rem" }}
          >
            Clear
          </Button>
        </Box>
      )}
      {/* Mode filter tabs */}
      <Tabs
        value={filterTab}
        onChange={(_, v) => onFilterChange(v)}
        variant="fullWidth"
        sx={{ px: 1 }}
      >
        {MODE_FILTERS.map((f) => (
          <Tab
            key={f.label}
            label={f.label}
            sx={{ minWidth: 0, fontSize: "0.8rem" }}
          />
        ))}
      </Tabs>
      <Divider />
      {/* Quick-filter tag chips */}
      {quickTags.length > 0 && !bulkMode && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              mr: 0.5,
              lineHeight: "24px"
            }}>
            Popular:
          </Typography>
          {quickTags.map((t) => (
            <Chip
              key={t.name}
              label={t.name}
              size="small"
              variant="outlined"
              onClick={() => onTagFilterAdd(t.name)}
              sx={{ height: 22, fontSize: "0.75rem", cursor: "pointer" }}
            />
          ))}
        </Box>
      )}
      {/* Bulk tag toolbar */}
      {bulkMode && (
        <BulkTagToolbar
          selectedCount={selectedIds.size}
          onApply={onBulkApply}
          onCancel={onBulkCancel}
        />
      )}
      {/* Conversation list */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <ConversationList
          conversations={conversations}
          isLoading={isLoading}
          selectedId={selectedId}
          onSelect={onSelect}
          onDelete={onDelete}
          onRename={onRename}
          onAddTag={onAddTag}
          onRemoveTag={onRemoveTag}
          onTagClick={onTagFilterAdd}
          tagSuggestions={tagSuggestions}
          hasFilters={hasFilters}
          bulkMode={bulkMode}
          selectedIds={selectedIds}
          onBulkToggle={onBulkToggle}
        />
      </Box>
    </Drawer>
  );
}
